import React, { useState } from 'react';
import './BollingerExitConditionsBar.css';

/**
 * Exit condition status for Bollinger Reversion trade groups
 */
export interface BollingerExitConditions {
  currentPrice: number;
  stopLoss?: number;
  middleBand?: number;  // Primary target (70% exit)
  upperBand?: number;   // Extended target (30% exit)
  isPartialExit: boolean; // Whether 70% has been sold
}

/**
 * Props for BollingerExitConditionsBar component
 */
export interface BollingerExitConditionsBarProps {
  /** Exit conditions data */
  exitConditions?: BollingerExitConditions;
  /** Whether data is currently loading */
  isLoading?: boolean;
  /** Optional CSS class name */
  className?: string;
}

/**
 * BollingerExitConditionsBar Component
 * 
 * Displays a visual indicator of exit condition proximity for Bollinger Reversion trade groups.
 * Shows 2-3 sections (Stop-Loss, Primary Target, Extended Target) with color-coded status.
 * 
 * @example
 * <BollingerExitConditionsBar 
 *   exitConditions={{
 *     currentPrice: 95000,
 *     stopLoss: 94000,
 *     middleBand: 96000,
 *     upperBand: 97000,
 *     isPartialExit: false
 *   }}
 * />
 */
const BollingerExitConditionsBar: React.FC<BollingerExitConditionsBarProps> = ({
  exitConditions,
  isLoading = false,
  className = '',
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Loading state: show skeleton
  if (isLoading) {
    return (
      <div className={`bollinger-exit-conditions-bar ${className}`}>
        <div className="exit-targets-display loading">
          <div className="exit-target-card skeleton">
            <div className="skeleton-shimmer"></div>
          </div>
          <div className="exit-target-card skeleton">
            <div className="skeleton-shimmer"></div>
          </div>
          <div className="exit-target-card skeleton">
            <div className="skeleton-shimmer"></div>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!exitConditions) {
    return null;
  }

  const { currentPrice, stopLoss, middleBand, upperBand, isPartialExit } = exitConditions;

  // Calculate proximity to each level (percentage distance)
  const calculateProximity = (targetPrice: number | undefined): number => {
    if (!targetPrice || !currentPrice || currentPrice <= 0) return 0;
    return ((targetPrice - currentPrice) / currentPrice) * 100;
  };

  const stopLossProximity = calculateProximity(stopLoss);
  const middleBandProximity = calculateProximity(middleBand);
  const upperBandProximity = calculateProximity(upperBand);

  // Determine status based on proximity
  const getStatus = (proximity: number): 'at-target' | 'near' | 'far' => {
    const absProximity = Math.abs(proximity);
    if (absProximity < 0.5) return 'at-target'; // Within 0.5%
    if (absProximity < 2.0) return 'near';      // Within 2%
    return 'far';                                // > 2%
  };

  // Build conditions array
  const conditions: Array<{
    label: string;
    shortLabel: string;
    value: number;
    proximity: number;
    status: 'at-target' | 'near' | 'far';
    critical: boolean;
  }> = [];

  // 1. Stop-Loss (always shown)
  if (stopLoss) {
    conditions.push({
      label: 'Stop Loss',
      shortLabel: 'STOP',
      value: stopLoss,
      proximity: stopLossProximity,
      status: getStatus(stopLossProximity),
      critical: stopLossProximity > -2.0, // Warn if within 2% of stop-loss
    });
  }

  // 2. Middle Band / Primary Target (70% exit) - only if not in partial exit
  if (middleBand && !isPartialExit) {
    conditions.push({
      label: 'Middle Band (70% Target)',
      shortLabel: '70%',
      value: middleBand,
      proximity: middleBandProximity,
      status: getStatus(middleBandProximity),
      critical: false,
    });
  }

  // 3. Upper Band / Extended Target (30% exit) - always shown when in position
  if (upperBand) {
    const label = isPartialExit ? 'Upper Band (30% Target)' : 'Upper Band (Extended)';
    const shortLabel = isPartialExit ? '30%' : 'EXT';
    conditions.push({
      label,
      shortLabel,
      value: upperBand,
      proximity: upperBandProximity,
      status: getStatus(upperBandProximity),
      critical: false,
    });
  }

  // Find next target (closest price above current)
  const nextTarget = conditions
    .filter(c => c.proximity > 0 && !c.critical)
    .sort((a, b) => a.proximity - b.proximity)[0];

  // Format currency helper
  const formatPrice = (price: number): string => {
    if (price < 1) {
      return `$${price.toFixed(4)}`;
    }
    return `$${price.toFixed(2)}`;
  };

  // Calculate summary text
  const getSummaryText = (): string => {
    if (nextTarget) {
      const targetName = nextTarget.label.includes('70%') ? 'Primary (70%)' : 
                        nextTarget.label.includes('30%') ? 'Extended (30%)' : 
                        nextTarget.label.includes('Extended') ? 'Extended' : 'Target';
      return `Next: ${targetName} at ${formatPrice(nextTarget.value)} (+${nextTarget.proximity.toFixed(2)}%)`;
    }
    // Check if any target is below current (stop loss)
    const stopLossTarget = conditions.find(c => c.critical && c.proximity < 0);
    if (stopLossTarget) {
      return `Stop Loss at ${formatPrice(stopLossTarget.value)} (${stopLossTarget.proximity.toFixed(2)}%)`;
    }
    return 'Monitoring exit targets';
  };

  return (
    <div className={`bollinger-exit-conditions-bar ${className}`}>
      {/* Summary header showing next target */}
      <div className="exit-targets-summary">
        {getSummaryText()}
      </div>

      {/* Exit Targets Display */}
      <div className="exit-targets-display">
        {conditions.map((condition, index) => {
          const { label, value, proximity, status, critical } = condition;
          const isNext = nextTarget && nextTarget.value === value;
          
          let cardClass = `exit-target-card ${status}`;
          if (critical) cardClass += ' critical';
          if (isNext) cardClass += ' next-target';

          // Color based on status
          let color = '#A78BFA'; // platform purple
          if (critical) color = '#ef4444'; // critical (red)
          else if (status === 'at-target') color = '#10b981'; // at target (green)
          else if (status === 'near') color = '#f59e0b'; // near (amber)

          return (
            <div
              key={index}
              className={cardClass}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              role="status"
              tabIndex={0}
            >
              <div className="target-label">{label}</div>
              <div className="target-price" style={{ color }}>{formatPrice(value)}</div>
              <div className="target-distance">
                {proximity > 0 ? '+' : ''}{proximity.toFixed(2)}%
                {isNext && ' (Next)'}
              </div>

              {/* Tooltip with full details */}
              {hoveredIndex === index && (
                <div className="exit-condition-tooltip" role="tooltip">
                  <div className="tooltip-header">{label}</div>
                  <div className="tooltip-detail">Price: {formatPrice(value)}</div>
                  <div className="tooltip-proximity">
                    Distance: {proximity > 0 ? '+' : ''}{proximity.toFixed(2)}%
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BollingerExitConditionsBar;
